/**
 * BankAccountsPage - Página de gestión de cuentas bancarias (CRUD)
 * Solo accesible por administradores
 */
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  ConfirmDialog,
  DataTable,
} from '../components/common';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as catalogoService from '../services/catalogoService';
import { cuentaBancariaSchema } from '../validations/catalogValidation';
import { formatCurrency, ACCOUNT_TYPE_LABELS, ROLES } from '../utils';

const BankAccountsPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cuentas, setCuentas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(cuentaBancariaSchema),
    defaultValues: {
      empresa_id: '',
      banco_id: '',
      numero_cuenta: '',
      tipo_cuenta: 'CORRIENTE',
      saldo_actual: 0,
      moneda: 'USD',
      titular: '',
      activo: true,
    },
  });

  // Observar cambios en empresa_id
  const empresaIdSeleccionada = watch('empresa_id');

  // Verificar que el usuario sea administrador
  useEffect(() => {
    if (user && user.rol !== ROLES.ADMINISTRADOR) {
      showError('No tiene permisos para acceder a esta página');
      window.history.back();
    }
  }, [user]);

  // Cargar cuentas bancarias y empresas
  const loadData = async () => {
    try {
      setLoading(true);
      const [cuentasData, empresasData] = await Promise.all([
        catalogoService.getCuentasBancarias(),
        catalogoService.getEmpresas({ activo: true }),
      ]);
      setCuentas(cuentasData);
      setEmpresas(empresasData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar bancos cuando cambia la empresa seleccionada
  useEffect(() => {
    const cargarBancos = async () => {
      if (empresaIdSeleccionada) {
        try {
          const bancosData = await catalogoService.getBancos({ activo: true });
          // Filtrar bancos por empresa seleccionada
          const bancosFiltrados = bancosData.filter(
            (banco) => banco.empresa_id === parseInt(empresaIdSeleccionada)
          );
          setBancos(bancosFiltrados);
          // Resetear banco_id si no existe en los nuevos bancos
          if (bancosFiltrados.length === 0) {
            setValue('banco_id', '');
          }
        } catch (error) {
          console.error('Error al cargar bancos:', error);
          setBancos([]);
        }
      } else {
        setBancos([]);
        setValue('banco_id', '');
      }
    };

    if (openDialog) {
      cargarBancos();
    }
  }, [empresaIdSeleccionada, openDialog, setValue]);

  useEffect(() => {
    loadData();
  }, []);

  // Abrir diálogo para crear
  const handleOpenCreate = () => {
    setEditingCuenta(null);
    reset({
      empresa_id: '',
      banco_id: '',
      numero_cuenta: '',
      tipo_cuenta: 'CORRIENTE',
      saldo_actual: 0,
      moneda: 'USD',
      titular: '',
      activo: true,
    });
    setOpenDialog(true);
  };

  // Abrir diálogo para editar
  const handleOpenEdit = (cuenta) => {
    setEditingCuenta(cuenta);
    reset({
      empresa_id: cuenta.empresa?.id || cuenta.empresa_id,
      banco_id: cuenta.banco?.id || cuenta.banco_id,
      numero_cuenta: cuenta.numero_cuenta || '',
      tipo_cuenta: cuenta.tipo_cuenta || 'CORRIENTE',
      saldo_actual: cuenta.saldo_actual || 0,
      moneda: cuenta.moneda || 'USD',
      titular: cuenta.titular || '',
      activo: cuenta.activo !== false,
    });
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCuenta(null);
    reset();
  };

  // Guardar cuenta bancaria (crear o actualizar)
  const onSubmit = async (data) => {
    try {
      if (editingCuenta) {
        await catalogoService.updateCuentaBancaria(editingCuenta.id, data);
        showSuccess('Cuenta bancaria actualizada correctamente');
      } else {
        await catalogoService.createCuentaBancaria(data);
        showSuccess('Cuenta bancaria creada correctamente');
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error al guardar cuenta bancaria:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al guardar cuenta bancaria';
      showError(errorMessage);
    }
  };

  // Confirmar eliminación
  const handleDelete = (cuenta) => {
    setDeleteConfirm(cuenta);
  };

  // Eliminar cuenta bancaria
  const confirmDelete = async () => {
    try {
      await catalogoService.deleteCuentaBancaria(deleteConfirm.id);
      showSuccess('Cuenta bancaria eliminada correctamente');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error al eliminar cuenta bancaria:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al eliminar cuenta bancaria';
      showError(errorMessage);
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      field: 'empresa',
      headerName: 'Empresa',
      renderCell: (row) => row.empresa?.nombre || '-',
    },
    {
      field: 'banco',
      headerName: 'Banco',
      renderCell: (row) => row.banco?.nombre || '-',
    },
    {
      field: 'numero_cuenta',
      headerName: 'Número de Cuenta',
    },
    {
      field: 'tipo_cuenta',
      headerName: 'Tipo',
      renderCell: (row) => ACCOUNT_TYPE_LABELS[row.tipo_cuenta] || row.tipo_cuenta,
    },
    {
      field: 'saldo_actual',
      headerName: 'Saldo',
      renderCell: (row) => formatCurrency(row.saldo_actual, row.moneda),
    },
    {
      field: 'activo',
      headerName: 'Estado',
      type: 'status',
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Cargando cuentas bancarias..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Cuentas Bancarias"
        breadcrumbs={[{ label: 'Cuentas Bancarias' }]}
        actions={[
          {
            label: 'Nueva Cuenta',
            icon: AddIcon,
            onClick: handleOpenCreate,
            variant: 'contained',
          },
        ]}
      />

      {/* Tabla de cuentas bancarias */}
      <DataTable
        columns={columns}
        data={cuentas}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar por banco o número de cuenta..."
        emptyMessage="No hay cuentas bancarias registradas"
      />

      {/* Diálogo de crear/editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingCuenta ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="empresa_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Empresa"
                      fullWidth
                      error={!!errors.empresa_id}
                      helperText={errors.empresa_id?.message}
                      autoFocus
                    >
                      <MenuItem value="">Seleccione una empresa</MenuItem>
                      {empresas.map((empresa) => (
                        <MenuItem key={empresa.id} value={empresa.id}>
                          {empresa.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="banco_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Banco"
                      fullWidth
                      error={!!errors.banco_id}
                      helperText={errors.banco_id?.message}
                      disabled={!empresaIdSeleccionada}
                    >
                      <MenuItem value="">
                        {empresaIdSeleccionada
                          ? 'Seleccione un banco'
                          : 'Primero seleccione una empresa'}
                      </MenuItem>
                      {bancos.length === 0 && empresaIdSeleccionada && (
                        <MenuItem value="" disabled>
                          No hay bancos disponibles para esta empresa
                        </MenuItem>
                      )}
                      {bancos.map((banco) => (
                        <MenuItem key={banco.id} value={banco.id}>
                          {banco.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('numero_cuenta')}
                  label="Número de Cuenta"
                  fullWidth
                  error={!!errors.numero_cuenta}
                  helperText={errors.numero_cuenta?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="tipo_cuenta"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Cuenta"
                      fullWidth
                      error={!!errors.tipo_cuenta}
                      helperText={errors.tipo_cuenta?.message}
                    >
                      <MenuItem value="AHORROS">Ahorros</MenuItem>
                      <MenuItem value="CORRIENTE">Corriente</MenuItem>
                      <MenuItem value="CHEQUES">Cheques</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="moneda"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Moneda"
                      fullWidth
                      error={!!errors.moneda}
                      helperText={errors.moneda?.message}
                    >
                      <MenuItem value="USD">Dólar (USD)</MenuItem>
                      <MenuItem value="VES">Bolívar (VES)</MenuItem>
                      <MenuItem value="EUR">Euro (EUR)</MenuItem>
                      <MenuItem value="ARS">Peso Argentino (ARS)</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('saldo_actual')}
                  label="Saldo Actual"
                  type="number"
                  fullWidth
                  error={!!errors.saldo_actual}
                  helperText={errors.saldo_actual?.message}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('titular')}
                  label="Titular (Opcional)"
                  fullWidth
                  error={!!errors.titular}
                  helperText={errors.titular?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('activo')}
                      defaultChecked={editingCuenta?.activo !== false}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingCuenta ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Eliminar Cuenta Bancaria"
        message={`¿Está seguro de que desea eliminar la cuenta "${deleteConfirm?.numero_cuenta}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </Box>
  );
};

export default BankAccountsPage;
